import type { Asset } from '@compass/client/types/asset';
import { AudioPlayer, type AudioPlayerHandle } from '@components/AudioPlayer/AudioPlayer';
import { DownloadDropdown } from '@components/DownloadDropdown';
import { ErrorContainer } from '@components/ErrorContainer';
import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@components/Header';
import { Transcript } from '@components/Transcript/Transcript';
import { Skeleton } from '@components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@components/ui/tabs';
import { useCall, useCaseRecordCalls } from '@hooks/useCalls';
import { useEvaluationAgents } from '@hooks/useEvaluationAgents';
import { useIs100msUser } from '@hooks/useIs100msUser';
import { useOrgAndPrograms } from '@hooks/useOrgAndPrograms';
import { useOrganizationUsers } from '@hooks/useOrganizationUsers';
import { removeOrgPrefix } from '@utils/routes';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { TranscriptSegment } from '@/hooks/useTranscript';
import { resolveTranscriptSeekTime } from '@/utils/transcript';

import { QcAddToDatasetDialog } from './components/QcAddToDatasetDialog';
import { QcDetailHeader } from './components/QcDetailHeader';
import { QcDetailScorecardTab } from './components/QcDetailScorecardTab';
import { QcDetailSummaryTab } from './components/QcDetailSummaryTab';
import { QcEvaluationPdfExportDialog } from './components/QcEvaluationPdfExportDialog';
import { QcFeedbackReviewsColumn } from './components/QcFeedbackReviewsColumn';
import { QcReviewActionsMenu } from './components/QcReviewAlerts';
import { useEvaluation } from './hooks/useEvaluation';
import { useEvaluationFeedback } from './hooks/useFeedback';
import { useQcDetailSummary } from './hooks/useQcDetailSummary';
import { useScorecard } from './hooks/useScorecard';
import { getAgentNameForEvaluation, resolveEvidenceHighlightRange } from './qcDetail.utils';
import { buildQcListPath, getMetadataValue, resolveQcOrganizationId } from './utils';

type QcDetailTab = 'summary' | 'scorecard';

const QcDetailBreadcrumb: React.FC<{
  qcListPath: string;
  label: string | undefined;
  callPath?: string;
}> = ({ qcListPath, label, callPath }) => (
  <BreadcrumbPortal>
    <Breadcrumb>
      <Breadcrumb.Item to={qcListPath}>Call Evaluations</Breadcrumb.Item>
      {label && <Breadcrumb.Item to={callPath}>{label}</Breadcrumb.Item>}
    </Breadcrumb>
  </BreadcrumbPortal>
);

const QcDetailLoadingState: React.FC = () => (
  <div className="flex h-full flex-col">
    <Skeleton className="mb-3 h-10" />
    <div className="flex min-h-0 flex-1 gap-0 overflow-hidden border">
      <div className="w-[40%] border-r p-4">
        <Skeleton className="h-8" />
        <Skeleton className="mt-4 h-96" />
      </div>
      <div className="flex-1 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-4 h-40" />
        <Skeleton className="mt-3 h-56" />
      </div>
    </div>
  </div>
);

export const QcDetailPage: React.FC = () => {
  const { evaluationId, orgId } = useParams<{ evaluationId?: string; orgId?: string }>();
  const [activeTab, setActiveTab] = useState<QcDetailTab>('scorecard');
  const [isFeedbackReviewsCollapsed, setIsFeedbackReviewsCollapsed] = useState(true);
  const [isPdfExportOpen, setIsPdfExportOpen] = useState(false);
  const [isAddToDatasetOpen, setIsAddToDatasetOpen] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [transcriptSegments, setTranscriptSegments] = useState<TranscriptSegment[]>([]);
  const [selectedEvidenceRange, setSelectedEvidenceRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [transcriptInsertTimestampSeconds, setTranscriptInsertTimestampSeconds] = useState<
    number | null
  >(null);
  const handleTranscriptInsertTimestampHandled = useCallback(() => {
    setTranscriptInsertTimestampSeconds(null);
  }, []);
  const audioPlayerRef = useRef<AudioPlayerHandle>(null);

  const cleanOrgIdFromRoute = orgId ? removeOrgPrefix(orgId) : null;
  const is100msUser = useIs100msUser();
  const { programs, selectedOrganizationId, isLoadingOrgAndPrograms, unauthorizedAccess } =
    useOrgAndPrograms();
  const resolvedOrganizationId = resolveQcOrganizationId(
    cleanOrgIdFromRoute,
    selectedOrganizationId,
  );

  const evaluationQuery = useEvaluation(evaluationId || null, resolvedOrganizationId);
  const evaluation = evaluationQuery.data?.evaluation;
  const contextOverride = useMemo(
    () => ({
      organizationId: evaluation?.organization_id || resolvedOrganizationId,
      programId: evaluation?.program_id || null,
    }),
    [evaluation?.organization_id, evaluation?.program_id, resolvedOrganizationId],
  );
  // TODO(AQ-540): Drop this org-wide GetEvaluationAgents call — the detail page only
  // needs one agent's name, so resolve it with a direct user-by-id lookup. (The
  // org-users list below can't stand in: it excludes inactive/deleted users.)
  // https://100ms.atlassian.net/browse/AQ-540
  //
  // Until then, scope the call to the evaluation's UTC day so it stays index-backed and
  // never scans the whole org (AQ-517); the viewed evaluation is inside this window, so
  // its agent still resolves, and the hook stays disabled until the bounds load.
  const evaluationAgentsDateBounds = useMemo(() => {
    // Derive the day from call_start_time (the axis GetEvaluationAgents now windows on,
    // AQ-512): using created_at here would build a call-start window around the wrong day
    // whenever the call happened on a different day than the eval was created (the exact
    // lag this migration addresses), and the agent lookup could then miss. Fall back to
    // created_at only when call_start_time is absent (old/un-backfilled rows).
    const anchor = evaluation?.call_start_time || evaluation?.created_at;
    if (!anchor) return undefined;
    const anchored = new Date(anchor);
    if (Number.isNaN(anchored.getTime())) return undefined;
    const dayStart = new Date(
      Date.UTC(
        anchored.getUTCFullYear(),
        anchored.getUTCMonth(),
        anchored.getUTCDate(),
        0,
        0,
        0,
        0,
      ),
    );
    const dayEnd = new Date(
      Date.UTC(
        anchored.getUTCFullYear(),
        anchored.getUTCMonth(),
        anchored.getUTCDate(),
        23,
        59,
        59,
        999,
      ),
    );
    return {
      callStartTimeAfter: dayStart.toISOString(),
      callStartTimeBefore: dayEnd.toISOString(),
      // Name lookup only: the evaluation being viewed may itself be ineligible (AQ-834).
      ineligible: true,
    };
  }, [evaluation?.call_start_time, evaluation?.created_at]);
  const { data: evaluationAgentsData } = useEvaluationAgents(
    contextOverride.organizationId || undefined,
    evaluationAgentsDateBounds,
  );
  const { data: orgUsersData } = useOrganizationUsers(contextOverride.organizationId || undefined, {
    limit: 500,
  });

  const caseCallsQuery = useCaseRecordCalls(evaluation?.case_record_id || null, {
    contextOverride,
  });
  const singleCallQuery = useCall(evaluation?.case_record_id || null, evaluation?.call_id || null, {
    contextOverride,
  });
  const callFromCaseCalls = useMemo(
    () =>
      caseCallsQuery.data?.calls?.find(candidate => candidate.id === evaluation?.call_id) || null,
    [caseCallsQuery.data?.calls, evaluation?.call_id],
  );
  const call = callFromCaseCalls || singleCallQuery.data?.call || null;
  const isLoadingCallAssets = caseCallsQuery.isLoading || singleCallQuery.isLoading;

  const scorecardQuery = useScorecard(
    evaluation?.program_id || null,
    evaluation?.scorecard_id || null,
  );
  const summaryQuery = useQcDetailSummary({
    caseId: evaluation?.case_record_id || null,
    callId: evaluation?.call_id || null,
    programId: evaluation?.program_id || null,
    enabled: !!evaluation?.case_record_id && !!evaluation?.call_id && !!evaluation?.program_id,
  });
  const feedbackQuery = useEvaluationFeedback(evaluation?.id || null);
  const userNameById = useMemo(() => {
    const map = new Map<string, string>();

    // Seed from evaluation agents (covers AI evaluator user IDs)
    for (const agent of evaluationAgentsData?.agents ?? []) {
      if (agent.user_id) {
        map.set(agent.user_id, agent.name || agent.email || agent.user_id);
      }
    }

    // Layer org users on top (covers human reviewers who leave feedback)
    for (const user of orgUsersData?.users ?? []) {
      if (user.user_id) {
        map.set(user.user_id, user.name || user.email || user.user_id);
      }
    }

    return map;
  }, [evaluationAgentsData?.agents, orgUsersData?.users]);

  const agentName = useMemo(
    () => (evaluation ? getAgentNameForEvaluation(evaluation, userNameById, call) : null),
    [evaluation, userNameById, call],
  );

  const programName = useMemo(() => {
    if (!evaluation?.program_id) {
      return null;
    }

    return (
      programs.find(program => program.id === evaluation.program_id)?.name || evaluation.program_id
    );
  }, [evaluation?.program_id, programs]);
  // Skill/campaign/team/queue are source context carried in the evaluation
  // metadata map (see HIDDEN_METADATA_KEYS in qcDetail.utils). Read them from
  // metadata so the same source powers the QC list and the detail view. skill/
  // campaign/team come from CXOne calls; queue-name is the Genesys equivalent.
  const skillName = useMemo(
    () => (evaluation ? getMetadataValue(evaluation, ['skill-name', 'skill_name']) : null),
    [evaluation],
  );
  const campaignName = useMemo(
    () => (evaluation ? getMetadataValue(evaluation, ['campaign-name', 'campaign_name']) : null),
    [evaluation],
  );
  const teamName = useMemo(
    () => (evaluation ? getMetadataValue(evaluation, ['team-name', 'team_name']) : null),
    [evaluation],
  );
  const queueName = useMemo(
    () => (evaluation ? getMetadataValue(evaluation, ['queue-name', 'queue_name']) : null),
    [evaluation],
  );

  const selectedCallEmbeddedAssets = call?.assets?.[0];
  const audioAssets = useMemo((): Asset[] => {
    const recordingId = selectedCallEmbeddedAssets?.recording;
    return recordingId ? ([{ id: recordingId }] as Asset[]) : [];
  }, [selectedCallEmbeddedAssets?.recording]);
  const transcriptAssets = useMemo((): Asset[] => {
    const transcriptId = selectedCallEmbeddedAssets?.transcript;
    return transcriptId ? ([{ id: transcriptId }] as Asset[]) : [];
  }, [selectedCallEmbeddedAssets?.transcript]);

  useEffect(() => {
    setActiveTab('scorecard');
    setAudioCurrentTime(0);
    setTranscriptSegments([]);
    setSelectedEvidenceRange(null);
  }, [evaluationId]);

  const qcListPath = buildQcListPath(cleanOrgIdFromRoute, resolvedOrganizationId);

  const breadcrumbLabel = evaluationId;

  const handleSeekTo = (seconds: number) => {
    setAudioCurrentTime(seconds);
    audioPlayerRef.current?.seekTo(seconds);
  };

  const handleTranscriptSegmentClick = (seconds: number) => {
    setTranscriptInsertTimestampSeconds(seconds);
    handleSeekTo(seconds);
  };

  const handleEvidenceSeek = (
    seekSeconds: number,
    highlightStartSeconds?: number,
    highlightEndSeconds?: number,
  ) => {
    const resolvedSeekSeconds = resolveTranscriptSeekTime(transcriptSegments, seekSeconds);
    setSelectedEvidenceRange({
      start: highlightStartSeconds ?? seekSeconds,
      end:
        highlightEndSeconds && highlightEndSeconds > (highlightStartSeconds ?? seekSeconds)
          ? highlightEndSeconds
          : (highlightStartSeconds ?? seekSeconds) + 1,
    });
    handleSeekTo(resolvedSeekSeconds);
  };

  const resolvedEvidenceHighlightRange = useMemo(() => {
    if (!selectedEvidenceRange) {
      return null;
    }

    return resolveEvidenceHighlightRange(
      transcriptSegments,
      selectedEvidenceRange.start,
      selectedEvidenceRange.end,
    );
  }, [selectedEvidenceRange, transcriptSegments]);

  const isPageLoading = isLoadingOrgAndPrograms || evaluationQuery.isLoading;

  if (unauthorizedAccess?.isUnauthorized) {
    return (
      <>
        <QcDetailBreadcrumb qcListPath={qcListPath} label={breadcrumbLabel} />
        <ErrorContainer errorMessage="You do not have access to this organization." />
      </>
    );
  }

  if (!evaluationId) {
    return (
      <>
        <QcDetailBreadcrumb qcListPath={qcListPath} label="Invalid Review" />
        <ErrorContainer errorMessage="Invalid QC evaluation ID." />
      </>
    );
  }

  if (isPageLoading) {
    return (
      <>
        <QcDetailBreadcrumb qcListPath={qcListPath} label={breadcrumbLabel} />
        <QcDetailLoadingState />
      </>
    );
  }

  if (evaluationQuery.isError || !evaluation) {
    return (
      <>
        <QcDetailBreadcrumb qcListPath={qcListPath} label={breadcrumbLabel} />
        <ErrorContainer errorMessage="Failed to load this QC evaluation." />
      </>
    );
  }

  return (
    <>
      <QcDetailBreadcrumb qcListPath={qcListPath} label={breadcrumbLabel} />
      <HeaderActionsPortal>
        <DownloadDropdown
          onlyShowIcon
          workflowType="pqc"
          onDownloadPDF={() => setIsPdfExportOpen(true)}
          pdfLabel="Export as PDF"
        />
      </HeaderActionsPortal>

      <div className="flex h-full flex-col overflow-hidden">
        <QcDetailHeader
          evaluation={evaluation}
          agentName={agentName}
          programName={programName}
          call={call}
        />

        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="flex h-full min-h-0 w-[41%] min-w-[18rem] max-w-[42rem] flex-col border-r bg-background">
            <div className="px-4 pt-3 pb-1">
              <h3 className="text-muted-foreground font-medium text-sm">Transcript</h3>
            </div>

            <div className="flex-1 min-h-0">
              <Transcript
                className="h-full"
                caseRecordId={evaluation.case_record_id || ''}
                callId={evaluation.call_id || ''}
                callSid={call?.sid}
                callStatus={call?.status}
                assets={transcriptAssets}
                isLoadingAssets={caseCallsQuery.isLoading || singleCallQuery.isLoading}
                currentTime={audioCurrentTime}
                onTranscriptLoad={setTranscriptSegments}
                onSegmentClick={handleTranscriptSegmentClick}
                contextOverride={contextOverride}
                enableSearch
                evidenceHighlightRange={resolvedEvidenceHighlightRange}
                onEvidenceHighlightClear={() => setSelectedEvidenceRange(null)}
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-muted/10">
            <div className="flex items-center justify-between gap-3 border-b px-4 py-2">
              <Tabs value={activeTab} onValueChange={value => setActiveTab(value as QcDetailTab)}>
                <TabsList>
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="scorecard">Scorecard</TabsTrigger>
                </TabsList>
              </Tabs>
              <QcReviewActionsMenu
                evaluation={evaluation}
                onAddToDataset={() => setIsAddToDatasetOpen(true)}
                canAddToDataset={is100msUser && !!evaluation.call_id}
              />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {activeTab === 'summary' ? (
                <QcDetailSummaryTab
                  evaluation={evaluation}
                  scorecard={scorecardQuery.data?.scorecard || null}
                  call={call}
                  callSummary={summaryQuery.summary}
                  evaluationFeedbacks={feedbackQuery.evaluationFeedbacks}
                  agentName={agentName}
                  programName={programName}
                  skillName={skillName}
                  campaignName={campaignName}
                  teamName={teamName}
                  queueName={queueName}
                  userNameById={userNameById}
                  showCaseBacklink
                />
              ) : (
                <QcDetailScorecardTab
                  evaluation={evaluation}
                  scorecard={scorecardQuery.data?.scorecard || null}
                  scorecardLoading={scorecardQuery.isLoading}
                  onEvidenceSeek={handleEvidenceSeek}
                  userNameById={userNameById}
                  transcriptInsertTimestampSeconds={transcriptInsertTimestampSeconds}
                  onTranscriptInsertTimestampHandled={handleTranscriptInsertTimestampHandled}
                />
              )}
            </div>
          </div>

          {/* The rail needs the full feedback row set so it can render the merged activity feed. */}
          <QcFeedbackReviewsColumn
            evaluation={evaluation}
            feedback={feedbackQuery.data}
            feedbackError={feedbackQuery.isError}
            feedbackLoading={feedbackQuery.isLoading}
            isCollapsed={isFeedbackReviewsCollapsed}
            onCollapsedChange={setIsFeedbackReviewsCollapsed}
            scorecard={scorecardQuery.data?.scorecard || null}
            userNameById={userNameById}
          />
        </div>

        <div className="shrink-0 border-t bg-background">
          <AudioPlayer
            ref={audioPlayerRef}
            caseRecordId={evaluation.case_record_id || ''}
            callId={evaluation.call_id || ''}
            callDetails={call}
            assets={audioAssets}
            isLoadingAssets={isLoadingCallAssets}
            onTimeUpdate={setAudioCurrentTime}
            contextOverride={contextOverride}
          />
        </div>
      </div>

      <QcEvaluationPdfExportDialog
        open={isPdfExportOpen}
        onOpenChange={setIsPdfExportOpen}
        evaluation={evaluation}
        scorecard={scorecardQuery.data?.scorecard || null}
        call={call}
        agentName={agentName}
      />

      <QcAddToDatasetDialog
        open={isAddToDatasetOpen}
        onOpenChange={setIsAddToDatasetOpen}
        callId={evaluation.call_id || null}
        scorecard={scorecardQuery.data?.scorecard || null}
      />
    </>
  );
};
